import { Tag } from 'sanitize-html';

import { removeModalLikeStyles } from 'app/utils/htmlSanitizer';
import {
  createSanitizationProfile,
  htmlTagGroups,
} from 'utils/sanitizeHtml/utils/createSanitizationProfile/createSanitizationProfile';

import { transformTagA } from '../../utils/transformTags';

let anchorTagContext: Tag | undefined = undefined;

/**
 * Represents the email and contact form channel specific sanitization profile.
 *
 * Note that this should be kept in sync with
 * https://github.com/dixahq/libtext/blob/86c1ea6cab241a54f5e67b98e1a7ca3961315793/src/main/scala/com/dixa/text/HtmlSanitizer.scala#L46
 */
export const emailSanitizationProfile = createSanitizationProfile({
  allowedTags: [
    ...Object.keys(htmlTagGroups).flatMap((name) => htmlTagGroups[name]),
    'a',
    'abbr',
    'acronym',
    'address',
    'area',
    'bdo',
    'big',
    'blockquote',
    'button',
    'center',
    'cite',
    'dd',
    'dfn',
    'dir',
    'fieldset',
    'font',
    'hr',
    'img',
    'input',
    'ins',
    'label',
    'legend',
    'map',
    'mark',
    'menu',
    'q',
    'small',
    'span',
    'style',
    'sub',
    'sup',
    'textarea',
    'u',
  ],
  allowedAttributes: {
    '*': [
      'style',
      'width',
      'height',
      'id',
      'class',
      'lang',
      'tabindex',
      'title',
      'dir',
      'color',
      'align',
      'bgcolor',
      'size',
      'face',
    ],
    abbr: ['title'],
    acronym: ['title'],
    area: ['shape', 'coords', 'href', 'target', 'alt', 'download'],
    bdo: ['fir'],
    blockquote: ['cite'],
    del: ['cite', 'datetime'],
    fieldset: ['disabled', 'form', 'name'],
    hr: ['align', 'color', 'noshade', 'size'],
    input: [
      'type',
      'disabled',
      'alt',
      'checked',
      'max',
      'min',
      'placeholder',
      'inputmode',
      'readonly',
      'required',
      'value',
      'pattern',
    ],
    ins: ['cite', 'datetime'],
    label: ['for', 'form'],
    li: ['value', 'type'],
    map: ['name'],
    menu: ['label', 'type'],
    ol: ['reversed', 'start', 'type'],
    optgroup: ['label', 'disabled'],
    option: ['value', 'label', 'selected'],
    q: ['cite'],
    select: ['disabled', 'name', 'required', 'size', 'multiple'],
    table: [
      'border',
      'cellpadding',
      'cellspacing',
      'frame',
      'rules',
      'summary',
    ],
    tbody: ['char', 'charoff', 'valign'],
    td: [
      'abbr',
      'axis',
      'char',
      'charoff',
      'colspan',
      'headers',
      'rowspan',
      'scope',
      'valign',
    ],
    textarea: [
      'autocapitalize',
      'cols',
      'disabled',
      'maxlength',
      'minlength',
      'name',
      'placeholder',
      'readonly',
      'required',
      'rows',
      'spellcheck',
      'wrap',
    ],
    tfoot: ['char', 'charoff', 'valign'],
    th: [
      'abbr',
      'axis',
      'char',
      'charoff',
      'valign',
      'colspan',
      'headers',
      'rowspan',
      'scope',
      'valign',
    ],
    thead: ['char', 'charoff', 'valign'],
    tr: ['char', 'charoff', 'valign'],
    ul: ['compact', 'type'],
    a: ['href', 'download', 'rel', 'hreflang', 'target'],
    img: ['src', 'alt', 'usemap'],
  },
  transformTags: {
    a: (tagName, attributes) => {
      anchorTagContext = transformTagA(tagName, attributes);
      return anchorTagContext;
    },
    '*': removeModalLikeStyles,
  },
  textFilter: (text, tagName) => {
    if (
      anchorTagContext &&
      anchorTagContext.tagName === tagName &&
      anchorTagContext.attribs &&
      'data-scheme-not-allowed' in anchorTagContext.attribs
    ) {
      return `${text} (${anchorTagContext.attribs.href})`;
    }

    return text;
  },
  allowVulnerableTags: true,
});
